import { css, html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { api } from './api.js';

import { ContextType } from '@lit/context';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { OrganizationContext } from './components/organization-context.js';
import { PickProjectName, ProjectSlugPicked } from './components/pick-project-name.js';
import { PickProjectVisibility } from './components/pick-project-visibility.js';
import { consume, contexts, TaskEvent } from './contexts.js';
import { integrationRepo, SourceCodeIntegration, SourceCodeIntegrationEvent } from './features/feature-source-code-integration.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { PageProjectTests } from './page-project-tests.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

function allOrNothing<T extends Record<string, any>>(obj: Partial<T>): Required<T> | undefined {
  for (const [key, value] of Object.entries(obj)) {
    if (!value)
      return undefined;
  }
  return obj as Required<T>;
}

@customElement('page-new-project')
export class PageNewProject extends LitElement {

  static url(options: { orgSlug: string }): string {
    return platformUrl(`/${options.orgSlug}/new-project`);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [ '/:org/new-project', ],
      render: (groups) => OrganizationContext.render({
        orgSlug: groups?.org,
      }, html`<page-new-project .orgSlug=${groups?.org}></page-new-project>`),
    }];
  }

  @property({ attribute: false }) orgSlug?: string;
  @query('pick-project-name') private _nameInput?: PickProjectName;
  @query('pick-project-visibility') private _visibility?: PickProjectVisibility;

  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.org, subscribe: true }) private _org?: ContextType<typeof contexts.org>;
  @consume({ context: contexts.router }) private _router?: ContextType<typeof contexts.router>;

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug] as const,
    metadata: ([orgSlug]) => orgSlug ? pageMetadata({ orgSlug, section: 'New Project', accessGated: true }) : undefined,
  });

  render() {
    if (!this._user)
      return;

    return html`
      <app-header .customTitle=${"New Project"}></app-header>
      <app-body>
        <h1>Create a new project</h1>
        <div class=project-details>
        <i>Projects</i> are containers for your test data, associated with a specific code repository. Each project stores test results, analytics, and history for one repository.
        </div>
        <form @submit=${this._submitForm}>
          
          <sl-divider></sl-divider>
          
          <!-- Project name container -->

          <!-- Project source  container -->
          <form-section step=${1} label="Source code integration" style="margin-top: var(--sl-spacing-large)">

            <feature-source-code-integration
              @source-code-integration=${this._onSourceCodeIntegration}
            ></feature-source-code-integration>

          </form-section>

          <form-section step=${2} label="Project name">
            <pick-project-name
              .orgSlug=${this.orgSlug}
              .initial=${integrationRepo(this._integration) ?? ''}
              @project-slug=${this._onProjectSlugPicked}
            ></pick-project-name>
          </form-section>

          <form-section step=${3} label="Project visibility">
            <pick-project-visibility
              .allowedVisibilities=${this._org?.allowedVisibilities}
            ></pick-project-visibility>
          </form-section>

          <sl-divider></sl-divider>

          <footer>
            <sl-button ?disabled=${!this._formData()} variant=success type=submit>Create project</sl-button>
          </footer>

        </form>
      </app-body>
      <app-footer></app-footer>
    `;
  }

  private _formData() {
    const project = allOrNothing({
      orgSlug: this._projectSlugResult?.data?.orgSlug,
      projectSlug: this._projectSlugResult?.data?.projectSlug,
    });
    if (project && this._integration)
      return { project, integration: this._integration };
    return undefined;
  }

  @state() private _integration?: SourceCodeIntegration;

  private async _onSourceCodeIntegration(event: SourceCodeIntegrationEvent) {
    this._integration = event.detail;
    // The feature retracts its pick when the user switches providers or edits a
    // token; there is nothing to prefill the project name from in that case.
    if (!event.detail)
      return;
    // Wait for Lit to push the new .initial into pick-project-name, then ask
    // it to validate so the Create button can enable without the user typing.
    await this.updateComplete;
    await this._nameInput?.validate();
  }

  @state() private _projectSlugResult?: TaskEvent<ProjectSlugPicked>;

  private _onProjectSlugPicked(event: TaskEvent<ProjectSlugPicked>) {
    this._projectSlugResult = event;
  }

  private async _submitForm(event: SubmitEvent) {
    event.preventDefault();

    const formData = this._formData();

    if (!formData)
      return;

    const common = {
      orgSlug: formData.project.orgSlug,
      projectSlug: formData.project.projectSlug,
      visibility: this._visibility?.visibility() ?? 'private',
      timeZoneId: Temporal.Now.timeZoneId(),
    };
    const integration = formData.integration;

    if (integration.kind === 'github_pat') {
      await api.organization.createGithubPATProject.POST({
        ...common,
        github: {
          repo: integration.repo,
          owner: integration.owner,
          accessToken: integration.accessToken,
        }
      });
    } else if (integration.kind === 'gitlab_pat') {
      await api.organization.createGitlabPATProject.POST({
        ...common,
        gitlab: {
          repo: integration.repo,
          owner: integration.owner,
          accessToken: integration.accessToken,
        }
      });
    } else if (integration.kind === 'github_app') {
      await api.organization.createGithubAppProject.POST({
        ...common,
        github: {
          repo: integration.repo,
          owner: integration.owner,
        }
      });
    } else if (integration.kind === 'local_checkout') {
      await api.organization.createLocalCheckoutProject.POST({
        ...common,
        defaultBranch: integration.defaultBranch,
        gitFolder: integration.gitFolder,
      });
    } else if (integration.kind === 'json_repo') {
      await api.organization.createJsonRepoProject.POST({
        ...common,
        jsonRepoPath: integration.jsonRepoPath,
      });
    } else {
      throw new Error('INTERNAL ERROR: weird form data');
    }

    this._router?.navigate(PageProjectTests.url({ orgSlug: this.orgSlug!, projectSlug: formData.project.projectSlug, snapshotId: undefined }));
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`
    form {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }

    footer {
      display: grid;
      grid-template-columns: 1fr auto;
      margin-bottom: 100px;

      & sl-button {
        grid-column: 1/-1;
      }
    }

    .project-details {
      color: var(--sl-color-neutral-400);
    }
  `];
}
